import { Injectable, OnDestroy } from '@angular/core';
import { AsyncSubject } from 'rxjs';
import {
	filter,
	mergeMap,
	pairwise,
	startWith,
	takeUntil,
	tap,
} from 'rxjs/operators';
import { StudentCourseProgressReportModel } from '../../api/students/classes/student-course-progress-report.model';
import { StudentAssignmentStoreService } from '../../stores/students/assignment/student-assignment-store.service';
import { StudentCourseFeedbackStoreService } from '../../stores/students/course-feedback/student-course-feedback-store.service';
import { StudentCourseStoreService } from '../../stores/students/course/student-course-store.service';
import { StudentPerformanceExpectationsStoreService } from '../../stores/students/expectations/student-performance-expectations-store.service';

@Injectable({
	providedIn: 'root',
})
export class CourseActivityCacheBusterService implements OnDestroy {
	private _unsubscribe$ = new AsyncSubject<null>();
	private _initialized = false;

	constructor(
		private studentCourseStore: StudentCourseStoreService,
		private studentPerformanceExpectationsStoreService: StudentPerformanceExpectationsStoreService,
		private studentAssignmentStoreService: StudentAssignmentStoreService,
		private studentCourseFeedbackStoreService: StudentCourseFeedbackStoreService,
	) {}

	public init = () => {
		if (this._initialized) {
			throw new Error(
				'CourseActivityCacheBusterService has already been initialized',
			);
		}
		this._initialized = true;

		this.watchForCourseUpdates();
	};

	public watchForCourseUpdates = () => {
		const bustClassCache = (uid: number, registrationId: number) => {
			this.studentCourseStore.refreshCurrent(uid);
			this.studentCourseStore.refreshCurrentPersistent(uid);

			this.studentPerformanceExpectationsStoreService.clearExpectations(uid);
			this.studentAssignmentStoreService.clearRecentlyGradedAssignments(uid);
			this.studentCourseFeedbackStoreService.clearRecentFeedback(uid);
			this.studentCourseFeedbackStoreService.clearRegistrationFeedback(
				uid,
				registrationId,
			);
		};

		this.studentCourseStore
			.classProgressReportCacheKeyAdded()
			.pipe(
				mergeMap(([uid, registrationId]) => {
					return this.studentCourseStore
						.classProgressReport$(uid, registrationId)
						.pipe(
							startWith(undefined as StudentCourseProgressReportModel),
							pairwise(),
							filter(([, newVal]) => newVal != null),
							tap(([oldVal, newVal]) => {
								if (oldVal === undefined) {
									bustClassCache(uid, registrationId);
								} else if (oldVal.current_grade !== newVal.current_grade) {
									bustClassCache(uid, registrationId);
								}
							}),
						);
				}),
				takeUntil(this._unsubscribe$),
			)
			.subscribe();
	};

	ngOnDestroy(): void {
		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
	}
}
